import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Separator } from "@veil/ui/components/separator";
import { cn } from "@veil/ui/lib/utils";
import {
	AlertTriangle,
	Check,
	ChevronDown,
	Copy,
	Home,
	RefreshCw,
} from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
	children: ReactNode;
	/** Optional scope label rendered in the header pill (e.g. "Dashboard"). */
	scope?: string;
	/** Override the default fallback. Receives the captured error + a reset fn. */
	fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
	/** Hook for telemetry (Sentry, console, etc.). */
	onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	error: Error | null;
	info: ErrorInfo | null;
	copied: boolean;
	showStack: boolean;
}

const INITIAL_STATE: ErrorBoundaryState = {
	error: null,
	info: null,
	copied: false,
	showStack: false,
};

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	override state: ErrorBoundaryState = INITIAL_STATE;

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { error };
	}

	override componentDidCatch(error: Error, info: ErrorInfo) {
		this.setState({ info });
		this.props.onError?.(error, info);
		console.error("[ErrorBoundary] caught render error", error, info);
	}

	private reset = () => {
		this.setState(INITIAL_STATE);
	};

	private reload = () => {
		window.location.reload();
	};

	private goHome = () => {
		window.location.assign("/");
	};

	private copyDetails = async () => {
		const { error, info } = this.state;
		if (!error) return;
		const payload = [
			`Message: ${error.message}`,
			`URL: ${window.location.href}`,
			`Time: ${new Date().toISOString()}`,
			"",
			"Stack:",
			error.stack ?? "(no stack)",
			"",
			"Component stack:",
			info?.componentStack ?? "(no component stack)",
		].join("\n");

		try {
			await navigator.clipboard.writeText(payload);
			this.setState({ copied: true });
			setTimeout(() => this.setState({ copied: false }), 1500);
		} catch {
			// no-op — clipboard can be unavailable in non-secure contexts
		}
	};

	private toggleStack = () => {
		this.setState((s) => ({ showStack: !s.showStack }));
	};

	override render() {
		const { error, info, copied, showStack } = this.state;
		if (!error) return this.props.children;

		if (this.props.fallback) {
			return this.props.fallback({ error, reset: this.reset });
		}

		const scope = this.props.scope ?? "Application";

		return (
			<div
				role="alert"
				aria-live="assertive"
				className="flex min-h-[60vh] w-full items-center justify-center p-4 md:p-6"
			>
				<Card className="w-full max-w-2xl border-destructive/30 shadow-sm">
					<CardHeader className="gap-3">
						<div className="flex items-start justify-between gap-3">
							<div className="flex items-center gap-3">
								<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
									<AlertTriangle className="size-5" aria-hidden />
								</div>
								<div className="flex flex-col gap-1">
									<CardTitle className="text-base">
										Something went wrong
									</CardTitle>
									<CardDescription>
										An unexpected error interrupted this view. Your data is
										safe; the rest of the app is still running.
									</CardDescription>
								</div>
							</div>
							<Badge variant="outline" className="font-mono text-xs">
								{scope}
							</Badge>
						</div>
					</CardHeader>

					<CardContent className="flex flex-col gap-4">
						<div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-mono text-xs leading-relaxed text-destructive">
							{error.name}: {error.message || "Unknown error"}
						</div>

						<button
							type="button"
							onClick={this.toggleStack}
							className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
							aria-expanded={showStack}
						>
							<ChevronDown
								className={cn(
									"size-3.5 transition-transform",
									showStack ? "rotate-0" : "-rotate-90",
								)}
								aria-hidden
							/>
							{showStack ? "Hide" : "Show"} technical details
						</button>

						{showStack ? (
							<div className="flex flex-col gap-3">
								<Separator />
								<div className="flex flex-col gap-1">
									<div className="text-xs font-medium text-muted-foreground">
										Stack trace
									</div>
									<pre className="max-h-48 overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
										{error.stack ?? "(no stack available)"}
									</pre>
								</div>
								{info?.componentStack ? (
									<div className="flex flex-col gap-1">
										<div className="text-xs font-medium text-muted-foreground">
											Component stack
										</div>
										<pre className="max-h-48 overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-[11px] leading-relaxed">
											{info.componentStack}
										</pre>
									</div>
								) : null}
							</div>
						) : null}
					</CardContent>

					<CardFooter className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
						<Button
							variant="ghost"
							size="sm"
							onClick={this.copyDetails}
							aria-label="Copy error details"
						>
							{copied ? (
								<Check className="size-3.5" aria-hidden />
							) : (
								<Copy className="size-3.5" aria-hidden />
							)}
							{copied ? "Copied" : "Copy details"}
						</Button>

						<div className="flex flex-wrap items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={this.goHome}
								aria-label="Return to dashboard"
							>
								<Home className="size-3.5" aria-hidden />
								Dashboard
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={this.reload}
								aria-label="Reload the page"
							>
								<RefreshCw className="size-3.5" aria-hidden />
								Reload
							</Button>
							<Button size="sm" onClick={this.reset} aria-label="Try again">
								Try again
							</Button>
						</div>
					</CardFooter>
				</Card>
			</div>
		);
	}
}
